import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LiveStatus, useStatus } from "./workspaceA11y.js";
import "./ConceptCanvas.css";

/**
 * ConceptCanvas — the Module 1 interactive exercise.
 *
 * This is the thing the learner actually DOES (not a quiz, not graded by an AI
 * judge). It carries its own light self-check feedback: explore every part, or
 * drag steps until they connect and "flow". When the exercise is satisfied it
 * calls `onComplete` once — that's what reveals the Next button.
 *
 * Driven by the AI-generated `canvas` spec on the cached lesson definition:
 *   canvas = {
 *     caption: string,
 *     mode: "explore" | "order",
 *     nodes: [{ id, label, icon, description }]   // for "order": in CORRECT order
 *   }
 *
 * Props:
 *   canvas             — the spec above
 *   onComplete()       — called once when the exercise is satisfied
 *   initiallyComplete  — render in the finished state (returning learner)
 *   onStateChange(s)   — reports live progress up to the Prompt Lab (optional)
 */

// Option B — every card shares one neutral surface; each node's hue survives only
// as a thin left-edge stripe, applied via the --stripe CSS var (see ConceptCanvas.css).
// Muted mid-tones so the accents read as a family, not a rainbow.
const PALETTE = ["#4a7fb5", "#3f9d6a", "#b58f4a", "#b5537e", "#3f9d9d", "#7a6cc4"];

// Drag auto-scroll: how close to the scroll container's edge the pointer has to
// get before the list starts scrolling itself, and how fast it does so.
const EDGE_ZONE = 64;   // px
const EDGE_SPEED = 14;  // px per frame, at the very edge

// Fisher–Yates; guarantees the result is not already in the solved order.
const shuffleUnsolved = (arr) => {
    if (arr.length < 2) return [...arr];
    const ids = arr.map((n) => n.id);
    let out = [...arr];
    for (let attempt = 0; attempt < 12; attempt++) {
        for (let i = out.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [out[i], out[j]] = [out[j], out[i]];
        }
        if (out.some((n, i) => n.id !== ids[i])) return out;
    }
    // Fallback: rotate by one so it's never the solved order.
    return [...out.slice(1), out[0]];
};

const ConceptCanvas = ({ canvas, onComplete, initiallyComplete = false, onStateChange, solveSignal }) => {
    const { t } = useTranslation();
    const nodes = Array.isArray(canvas?.nodes) ? canvas.nodes : [];
    const mode = canvas?.mode === "order" ? "order" : "explore";
    const correctIds = nodes.map((n) => n.id);

    // Fire onComplete at most once (already "armed" for a returning learner).
    const completedRef = useRef(initiallyComplete);
    const fireComplete = () => {
        if (completedRef.current) return;
        completedRef.current = true;
        onComplete?.();
    };

    // --- explore mode state ---
    const [openId, setOpenId] = useState(null);
    const [exploredIds, setExploredIds] = useState(
        () => new Set(initiallyComplete ? correctIds : [])
    );

    // --- order mode state ---
    const [order, setOrder] = useState(() =>
        mode === "order" ? (initiallyComplete ? [...nodes] : shuffleUnsolved(nodes)) : []
    );
    const [dragIndex, setDragIndex] = useState(null);

    // --- order mode: pointer-driven drag ---
    // HTML5 drag-and-drop (draggable + dragstart/drop) emits nothing at all for
    // touch input on iOS Safari or Android Chrome, which left the ▲ ▼ buttons as
    // the only working reorder path on a phone — while the caption still said to
    // drag. Pointer Events give mouse, touch and pen one identical code path.
    //
    // Everything the rAF loop below touches is held in a ref: the loop is
    // scheduled once at drag start, so its closure never sees a later render's
    // `order`. Reorders go through the functional form of setOrder for the same
    // reason.
    const stepRefs = useRef([]);   // live DOM nodes, by visual position
    const dragRef = useRef(null);  // { pointerId, index, grabOffsetY, dy, pending }
    const pointerYRef = useRef(0);
    const rafRef = useRef(0);
    const scrollerRef = useRef(null);
    // The ▲/▼ buttons by step id and direction, the move to refocus after, and
    // what the last move did, for screen readers.
    const nudgeRefs = useRef({});
    const nudgeFocus = useRef(null);
    const [moveNote, announceMove] = useStatus();

    const swapInOrder = (a, b) =>
        setOrder((prev) => {
            const next = [...prev];
            [next[a], next[b]] = [next[b], next[a]];
            return next;
        });

    // Nearest scrollable ancestor: the canvas pane scrolls on desktop, but once
    // the layout stacks (≤900px) nothing between here and the page scrolls, and
    // null means "scroll the window" instead.
    const findScroller = (el) => {
        for (let p = el?.parentElement; p; p = p.parentElement) {
            const overflowY = window.getComputedStyle(p).overflowY;
            if ((overflowY === "auto" || overflowY === "scroll") && p.scrollHeight > p.clientHeight) {
                return p;
            }
        }
        return null;
    };

    // Glue the dragged card to the pointer. Its own transform is subtracted out
    // first so `layoutTop` is where the card sits in normal flow — which is what
    // moves, on its own, every time a swap commits. Re-deriving it each frame is
    // what makes the card survive a swap without jumping.
    const applyDragTransform = () => {
        const drag = dragRef.current;
        // While a swap is in flight `stepRefs` still describes the pre-swap
        // layout, so `layoutTop` would be a frame stale. Sit the frame out; the
        // layout effect below re-applies as soon as the new order commits.
        if (!drag || drag.pending || !drag.el) return;
        const layoutTop = drag.el.getBoundingClientRect().top - drag.dy;
        drag.dy = pointerYRef.current - drag.grabOffsetY - layoutTop;
        drag.el.style.transform = `translateY(${drag.dy}px)`;
    };

    // Steps can easily run past the fold on a phone, where the whole page is the
    // scroller — without this, a step can't be dragged anywhere it isn't already.
    const autoScroll = () => {
        const scroller = scrollerRef.current;
        const { top, bottom } = scroller
            ? scroller.getBoundingClientRect()
            : { top: 0, bottom: window.innerHeight };
        const y = pointerYRef.current;

        let delta = 0;
        if (y < top + EDGE_ZONE) {
            delta = -EDGE_SPEED * Math.min(1, (top + EDGE_ZONE - y) / EDGE_ZONE);
        } else if (y > bottom - EDGE_ZONE) {
            delta = EDGE_SPEED * Math.min(1, (y - (bottom - EDGE_ZONE)) / EDGE_ZONE);
        }
        if (!delta) return;

        if (scroller) scroller.scrollTop += delta;
        else window.scrollBy(0, delta);
    };

    // Swap with whichever neighbour's midpoint the pointer has crossed. Only the
    // two immediate neighbours are considered, so the list settles one step at a
    // time instead of thrashing. Neighbours are never transformed, so their rects
    // are their true layout boxes.
    const maybeSwap = () => {
        const drag = dragRef.current;
        if (!drag || drag.pending) return;
        const y = pointerYRef.current;

        for (const to of [drag.index - 1, drag.index + 1]) {
            const el = stepRefs.current[to];
            if (!el) continue;
            const rect = el.getBoundingClientRect();
            const mid = rect.top + rect.height / 2;
            const crossed = to < drag.index ? y < mid : y > mid;
            if (!crossed) continue;

            // Hold off further swaps until React commits this one — until then
            // stepRefs still describes the pre-swap layout.
            drag.pending = true;
            const from = drag.index;
            drag.index = to;
            swapInOrder(from, to);
            return;
        }
    };

    const dragFrame = () => {
        if (!dragRef.current) return;
        autoScroll();
        maybeSwap();
        applyDragTransform();
        rafRef.current = requestAnimationFrame(dragFrame);
    };

    const stopDrag = () => {
        const drag = dragRef.current;
        if (!drag) return;
        cancelAnimationFrame(rafRef.current);
        if (drag.el) drag.el.style.transform = "";
        dragRef.current = null;
        scrollerRef.current = null;
        setDragIndex(null);
    };

    const startDrag = (e, i) => {
        // Leave the ▲ ▼ buttons their own clicks.
        if (e.target.closest("button")) return;
        // A fresh pointerdown means any earlier gesture is over — even if its
        // pointerup never reached us, which can happen when the browser refused
        // capture and the release landed outside every step. Bailing out here
        // instead would strand the drag state and kill reordering for good.
        stopDrag();

        const fromGrip = !!e.target.closest(".concept-canvas-step-grip");
        // On touch and pen the card body has to stay free for page scrolling, so
        // only the grip — which sets `touch-action: none` — starts a drag there.
        // A mouse has no such conflict and can still grab the card anywhere.
        if (!fromGrip && e.pointerType !== "mouse") return;

        const el = e.currentTarget;
        // Capture so the drag survives the pointer leaving the card, which it
        // does constantly: the list reorders out from under the finger. Throws if
        // the pointer already went away between dispatch and here — best-effort,
        // since the drag is still perfectly usable without it.
        try {
            el.setPointerCapture?.(e.pointerId);
        } catch {
            /* no capture; events still arrive while the pointer is over a step */
        }
        scrollerRef.current = findScroller(el);
        pointerYRef.current = e.clientY;
        dragRef.current = {
            pointerId: e.pointerId,
            // The card's own DOM node. React keys these by node id, so this stays
            // the dragged card no matter how the order shuffles — unlike an index
            // into stepRefs, which points at whoever currently occupies the slot.
            el,
            index: i,
            grabOffsetY: e.clientY - el.getBoundingClientRect().top,
            dy: 0,
            pending: false,
        };
        setDragIndex(i);
        rafRef.current = requestAnimationFrame(dragFrame);
    };

    const moveDrag = (e) => {
        if (dragRef.current?.pointerId !== e.pointerId) return;
        pointerYRef.current = e.clientY;
    };

    const endDrag = (e) => {
        if (dragRef.current?.pointerId !== e.pointerId) return;
        stopDrag();
    };

    // A swap has landed: clear the hold and re-glue the card before paint, so the
    // reorder reads as the card staying put while its neighbour slides past.
    useLayoutEffect(() => {
        if (!dragRef.current) return;
        dragRef.current.pending = false;
        applyDragTransform();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order]);

    useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

    // A move into the first or last place disables the button that was pressed,
    // so focus goes to the same step's other button.
    useEffect(() => {
        const pending = nudgeFocus.current;
        if (!pending) return;
        nudgeFocus.current = null;
        const pressed = nudgeRefs.current[`${pending.id}:${pending.dir}`];
        if (pressed && !pressed.disabled) return;
        nudgeRefs.current[`${pending.id}:${-pending.dir}`]?.focus();
    }, [order]);

    const solved =
        mode === "order" &&
        order.length === nodes.length &&
        order.every((n, i) => n.id === correctIds[i]);

    // Completion effects
    useEffect(() => {
        if (mode === "explore" && nodes.length > 0 && exploredIds.size >= nodes.length) {
            fireComplete();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [exploredIds, mode, nodes.length]);

    useEffect(() => {
        if (mode === "order" && solved) fireComplete();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [solved, mode]);

    // "Do it for me" from the navbar's "Stuck?" panel. The answer is authored, not
    // generated: `nodes` IS the correct order, and exploring everything is what an
    // explore canvas asks for. The completion effects above then fire as normal.
    useEffect(() => {
        if (!solveSignal) return;
        if (mode === "order") setOrder([...nodes]);
        else setExploredIds(new Set(correctIds));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [solveSignal]);

    // Report a compact snapshot of the learner's progress to the parent so the
    // Prompt Lab chat can tell Claude what they've actually done on this canvas.
    // The parent stores it in a ref (never state), so emitting can't loop renders.
    useEffect(() => {
        if (!onStateChange) return;
        if (mode === "explore") {
            const exploredLabels = [];
            const remainingLabels = [];
            nodes.forEach((n, i) => {
                const key = n.id || String(i);
                (exploredIds.has(key) ? exploredLabels : remainingLabels).push(n.label);
            });
            onStateChange({
                mode: "explore",
                total: nodes.length,
                exploredLabels,
                remainingLabels,
                allExplored: exploredIds.size >= nodes.length,
            });
        } else {
            onStateChange({
                mode: "order",
                currentOrderLabels: order.map((n) => n.label),
                solved,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onStateChange, mode, exploredIds, order, solved, nodes.length]);

    if (nodes.length === 0) {
        return <div className="concept-canvas-empty">{t("promptLab.canvasLoading")}</div>;
    }

    // ----------------------------------------------------------------- explore
    if (mode === "explore") {
        const allExplored = exploredIds.size >= nodes.length;

        const tap = (key) => {
            setOpenId((cur) => (cur === key ? null : key));
            setExploredIds((prev) => {
                if (prev.has(key)) return prev;
                const next = new Set(prev);
                next.add(key);
                return next;
            });
        };

        return (
            <div className="concept-canvas">
                {canvas?.caption && <p className="concept-canvas-caption">{canvas.caption}</p>}

                <div className="concept-canvas-nodes">
                    {nodes.map((node, i) => {
                        const key = node.id || String(i);
                        const color = PALETTE[i % PALETTE.length];
                        const isOpen = openId === key;
                        const explored = exploredIds.has(key);
                        return (
                            <button
                                key={key}
                                type="button"
                                className={`concept-canvas-node ${isOpen ? "is-open" : ""} ${explored ? "is-explored" : ""}`}
                                style={{ "--stripe": color }}
                                onClick={() => tap(key)}
                                aria-expanded={isOpen}
                            >
                                <span className="concept-canvas-node-label">
                                    {node.icon && <span className="concept-canvas-node-icon">{node.icon}</span>}
                                    {node.label}
                                    {explored && <span className="concept-canvas-node-check">✓</span>}
                                </span>
                                {isOpen && node.description && (
                                    <span className="concept-canvas-node-desc">{node.description}</span>
                                )}
                            </button>
                        );
                    })}
                </div>

                <p className={`concept-canvas-feedback ${allExplored ? "is-done" : ""}`}>
                    {allExplored
                        ? t("promptLab.canvasExploredAll")
                        : t("promptLab.canvasExploreProgress", { done: exploredIds.size, total: nodes.length })}
                </p>
            </div>
        );
    }

    // ------------------------------------------------------------------- order
    const correctIndex = (node) => correctIds.indexOf(node.id);

    const nudge = (i, dir) => {
        const to = i + dir;
        if (to < 0 || to >= order.length) return;
        const node = order[i];
        const next = [...order];
        [next[i], next[to]] = [next[to], next[i]];
        nudgeFocus.current = { id: node.id, dir };
        swapInOrder(i, to);
        const where = t("promptLab.canvasPosition", { item: node.label, position: to + 1, total: order.length });
        const nowSolved = next.every((n, k) => n.id === correctIds[k]);
        announceMove(nowSolved ? `${where}. ${t("promptLab.canvasSolvedNote")}` : where);
    };

    return (
        <div className="concept-canvas">
            {canvas?.caption && <p className="concept-canvas-caption">{canvas.caption}</p>}

            <div className="concept-canvas-steps">
                {order.map((node, i) => {
                    const key = node.id || String(i);
                    const color = PALETTE[correctIndex(node) % PALETTE.length];
                    // The connector ABOVE this step flows when the previous step is its
                    // true predecessor in the correct sequence.
                    const connectsUp =
                        i > 0 && correctIndex(node) === correctIndex(order[i - 1]) + 1;

                    return (
                        <React.Fragment key={key}>
                            {i > 0 && (
                                <div
                                    className={`concept-canvas-connector ${connectsUp ? "is-connected" : "is-broken"}`}
                                    aria-hidden="true"
                                >
                                    ↓
                                </div>
                            )}
                            <div
                                ref={(el) => { stepRefs.current[i] = el; }}
                                className={`concept-canvas-step ${dragIndex === i ? "is-dragging" : ""} ${solved ? "is-solved" : ""}`}
                                style={{ "--stripe": color }}
                                onPointerDown={(e) => startDrag(e, i)}
                                onPointerMove={moveDrag}
                                onPointerUp={endDrag}
                                onPointerCancel={endDrag}
                                onLostPointerCapture={endDrag}
                            >
                                <span className="concept-canvas-step-grip" aria-hidden="true">⠿</span>
                                <span className="concept-canvas-step-body">
                                    <span className="concept-canvas-node-label">
                                        {node.icon && <span className="concept-canvas-node-icon">{node.icon}</span>}
                                        {node.label}
                                    </span>
                                    {node.description && (
                                        <span className="concept-canvas-step-desc">{node.description}</span>
                                    )}
                                </span>
                                <span className="concept-canvas-step-nudge">
                                    <button
                                        type="button"
                                        ref={(el) => { nudgeRefs.current[`${node.id}:-1`] = el; }}
                                        aria-label={`${t("promptLab.moveUp")}: ${node.label}`}
                                        disabled={i === 0}
                                        onClick={() => nudge(i, -1)}
                                    >▲</button>
                                    <button
                                        type="button"
                                        ref={(el) => { nudgeRefs.current[`${node.id}:1`] = el; }}
                                        aria-label={`${t("promptLab.moveDown")}: ${node.label}`}
                                        disabled={i === order.length - 1}
                                        onClick={() => nudge(i, 1)}
                                    >▼</button>
                                </span>
                            </div>
                        </React.Fragment>
                    );
                })}
            </div>

            <p className={`concept-canvas-feedback ${solved ? "is-done" : ""}`}>
                {solved
                    ? t("promptLab.canvasSolved")
                    : t("promptLab.canvasOrderHint")}
            </p>
            <LiveStatus message={moveNote} />
        </div>
    );
};

export default ConceptCanvas;
